import React, { useState, useEffect, useCallback } from 'react';
import { X, Columns, CheckCircle, MinusCircle, AlertTriangle, ClipboardCheck, ChevronRight, Maximize2, Minimize2, Moon, Sun } from 'lucide-react';
import type { ColumnMappingEntry } from '../../types/rosterFlow';
import { ROSTER_STANDARD_FIELDS } from '../../types/rosterFlow';

// Sentinel — means this standard field has no Excel column mapped to it
const NONE = '__NONE__';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** One row per standard roster field; sourceCol defaults to NONE. */
function buildInitialMappings(_columns: string[]): ColumnMappingEntry[] {
  return ROSTER_STANDARD_FIELDS.map((field, i) => ({
    id:           `field_${i}`,
    sourceCol:    NONE,
    systemField:  field,
    sampleValues: [],
    isAutoMapped: false,
  }));
}

function getSampleValues(col: string, rows: Record<string, any>[], max = 3): string[] {
  const vals: string[] = [];
  for (const row of rows) {
    if (vals.length >= max) break;
    const v = row[col];
    if (v !== undefined && v !== null && String(v).trim() !== '')
      vals.push(String(v).trim().slice(0, 28));
  }
  return vals;
}

// ─── Shared CSS ──────────────────────────────────────────────────────────────

const injectStyles = () => {
  if (typeof document !== 'undefined' && !document.getElementById('hm-modal-styles')) {
    const el = document.createElement('style');
    el.id = 'hm-modal-styles';
    el.textContent = `
      @keyframes hm-fadeIn { from{opacity:0}to{opacity:1} }
      @keyframes hm-slideUp {
        from{opacity:0;transform:translateY(32px) scale(0.97)}
        to  {opacity:1;transform:translateY(0)    scale(1)   }
      }
      .hm-select{
        padding:7px 10px;font-size:13px;font-weight:500;
        border:1.5px solid #e5e7eb;border-radius:7px;background:#fff;
        cursor:pointer;transition:border-color 0.18s,box-shadow 0.18s;
        color:#0f172a;width:100%;max-width:220px;
      }
      .hm-select:hover{border-color:#002677;}
      .hm-select:focus{outline:none;border-color:#002677;box-shadow:0 0 0 3px rgba(0,38,119,0.12);}
      .hm-tr:hover td{background:rgba(0,38,119,0.025);}
      .hm-dark .hm-select{background:#1e293b;color:#e2e8f0;border-color:#334155;}
      .hm-dark .hm-select:hover{border-color:#93c5fd;}
      .hm-dark .hm-select:focus{border-color:#93c5fd;box-shadow:0 0 0 3px rgba(147,197,253,0.15);}
      .hm-dark .hm-tr:hover td{background:rgba(147,197,253,0.06);}
    `;
    document.head.appendChild(el);
  }
};

// ─── Step Indicator ──────────────────────────────────────────────────────────

const FLOW_STEPS = ['Column Mapping', 'Validation', 'Load'];

const StepIndicator: React.FC<{ current: number; completedSteps?: boolean[]; onNavigate?: (step: number) => void }> = ({ current, completedSteps = [], onNavigate }) => (
  <div style={{ display:'flex', alignItems:'center' }}>
    {FLOW_STEPS.map((label, i) => {
      const isDone = completedSteps[i] === true;
      const active = i === current;
      const canNav = isDone && !active && !!onNavigate;
      return (
        <React.Fragment key={i}>
          <div
            onClick={canNav ? () => onNavigate!(i) : undefined}
            title={canNav ? `Go to ${label}` : undefined}
            style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3, cursor: canNav ? 'pointer' : 'default' }}
          >
            <div style={{
              width:26, height:26, borderRadius:'50%',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, fontWeight:700,
              background: active ? 'white' : isDone ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.2)',
              color: active || isDone ? '#002677' : 'rgba(255,255,255,0.6)',
              boxShadow: active ? '0 0 0 3px rgba(255,255,255,0.3)' : canNav ? '0 0 0 2px rgba(255,255,255,0.4)' : 'none',
            }}>
              {i < current ? <CheckCircle size={13} color="#002677" /> : i + 1}
            </div>
            <span style={{ fontSize:10, fontWeight: active ? 700 : isDone ? 600 : 400, color: active ? 'white' : isDone ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', whiteSpace:'nowrap' }}>{label}</span>
          </div>
          {i < FLOW_STEPS.length - 1 && (
            <div style={{ width:36, height:2, background: completedSteps[i] ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)', margin:'0 4px 13px' }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Status Badge ────────────────────────────────────────────────────────────

const MappingBadge: React.FC<{ systemField: string }> = ({ systemField }) => {
  if (systemField === 'IGNORE') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 9px', borderRadius:20, background:'#f1f5f9', color:'#64748b', fontSize:11, fontWeight:600 }}>
      <MinusCircle size={11} /> Ignored
    </span>
  );
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 9px', borderRadius:20, background:'#dcfce7', color:'#15803d', fontSize:11, fontWeight:600 }}>
      <CheckCircle size={11} /> Mapped
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

export interface HeaderMappingModalProps {
  isOpen: boolean;
  columns: string[];
  rows: Record<string, any>[];
  fileName: string;
  /** Pre-built mappings from the API — one entry per standard field. */
  initialMappings?: ColumnMappingEntry[];
  onConfirm: (mappings: ColumnMappingEntry[]) => void;
  onClose: () => void;
  /** When true the Proceed to Validation button is disabled (already submitted). */
  submitted?: boolean;
  /** [columnMapping, validation, load] — true means that stage has data and can be navigated to. */
  completedSteps?: boolean[];
  /** Called when the user clicks a completed stage in the step indicator to navigate there. */
  onNavigate?: (step: number) => void;
}

export const HeaderMappingModal: React.FC<HeaderMappingModalProps> = ({
  isOpen, columns, rows, initialMappings, fileName, onConfirm, onClose, submitted = false, completedSteps = [], onNavigate,
}) => {
  const [mappings, setMappings] = useState<ColumnMappingEntry[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDark, setIsDark] = useState(false);

  const toggleExpand = useCallback((id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (initialMappings && initialMappings.length > 0) {
      // Attach sample values from rows using the pre-filled sourceCol
      setMappings(initialMappings.map(m => ({
        ...m,
        sampleValues: m.sourceCol !== NONE ? getSampleValues(m.sourceCol, rows) : [],
      })));
    } else {
      setMappings(buildInitialMappings(columns));
    }
  }, [isOpen, initialMappings, columns, rows]);

  // When user changes the Excel column for a row, also refresh sample values.
  // Enforce uniqueness: if another row already uses newSourceCol, clear it there first.
  const handleColChange = useCallback((id: string, newSourceCol: string) => {
    setMappings(prev => prev.map(m => {
      if (m.id === id) {
        return {
          ...m,
          sourceCol:      newSourceCol,
          isAutoMapped:   false,
          requiresReview: false,
          sampleValues:   newSourceCol !== NONE ? getSampleValues(newSourceCol, rows) : [],
        };
      }
      // Clear this column from any other row that already has it selected
      if (newSourceCol !== NONE && m.sourceCol === newSourceCol) {
        return { ...m, sourceCol: NONE, isAutoMapped: false, requiresReview: false, sampleValues: [] };
      }
      return m;
    }));
  }, [rows]);

  if (!isOpen) return null;

  const t = isDark ? {
    bodyBg:         'linear-gradient(145deg,#0f172a 0%,#1a2744 100%)',
    theadBg:        '#0f172a',
    theadColor:     '#93c5fd',
    theadBorder:    '#334155',
    rowBorder:      '#1e293b',
    rowNumColor:    '#475569',
    footerBg:       '#0f172a',
    footerBorder:   '#334155',
    footerText:     '#94a3b8',
    cancelBg:       '#1e293b',
    cancelBorder:   '#475569',
    cancelColor:    '#e2e8f0',
    targetBadgeBg:  '#172554',
    targetBadgeColor:'#93c5fd',
    targetBadgeBorder:'#1d4ed8',
    sampleBg:       '#1e293b',
    sampleBorder:   '#334155',
    sampleColor:    '#94a3b8',
    emptyText:      '#475569',
    noDataColor:    '#475569',
    aiBadgeBg:      '#1e3a5f',
    aiBadgeColor:   '#93c5fd',
    reviewBadgeBg:  '#431407',
    reviewBadgeColor:'#fb923c',
  } : {
    bodyBg:         'linear-gradient(145deg,#ffffff 0%,#f8fafc 100%)',
    theadBg:        '#f1f5f9',
    theadColor:     '#1e40af',
    theadBorder:    '#e5e7eb',
    rowBorder:      '#f1f5f9',
    rowNumColor:    '#94a3b8',
    footerBg:       '#f9fafb',
    footerBorder:   '#e5e7eb',
    footerText:     '#64748b',
    cancelBg:       'white',
    cancelBorder:   '#e5e7eb',
    cancelColor:    '#374151',
    targetBadgeBg:  '#eff6ff',
    targetBadgeColor:'#002677',
    targetBadgeBorder:'#bfdbfe',
    sampleBg:       '#f8fafc',
    sampleBorder:   '#e2e8f0',
    sampleColor:    '#475569',
    emptyText:      '#94a3b8',
    noDataColor:    '#cbd5e1',
    aiBadgeBg:      '#eff6ff',
    aiBadgeColor:   '#2563eb',
    reviewBadgeBg:  '#fef3c7',
    reviewBadgeColor:'#92400e',
  };

  const mapped       = mappings.filter(m => m.sourceCol !== NONE).length;
  const unmapped     = mappings.filter(m => m.sourceCol === NONE).length;
  const reviewNeeded = mappings.filter(m => m.requiresReview).length;
  const aiMapped     = mappings.filter(m => m.isAutoMapped).length;


  return (
    <div style={{
      position:'fixed', inset:0,
      background:'linear-gradient(135deg,rgba(15,23,42,0.92) 0%,rgba(30,41,59,0.88) 100%)',
      backdropFilter:'blur(16px) saturate(180%)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:999999, padding: isFullscreen ? 0 : 20,
      animation:'hm-fadeIn 0.25s ease',
    }}>
      <div className={isDark ? 'hm-dark' : ''} style={{
        background: t.bodyBg,
        borderRadius: isFullscreen ? 0 : 24, overflow:'hidden',
        display:'flex', flexDirection:'column',
        width: isFullscreen ? '100vw' : '94vw',
        maxWidth: isFullscreen ? '100vw' : 1020,
        height: isFullscreen ? '100vh' : '90vh',
        boxShadow:'0 40px 100px rgba(15,23,42,0.5),0 0 0 1px rgba(255,255,255,0.08)',
        animation:'hm-slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}>

        {/* ── Header ── */}
        <div style={{ background:'rgb(0,38,119)', padding:'20px 28px', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18 }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:11, background:'rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Columns size={22} color="white" />
              </div>
              <div>
                <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'white', letterSpacing:'-0.3px' }}>Column Mapping</h2>
                <p style={{ margin:'3px 0 0', fontSize:12, color:'rgba(255,255,255,0.7)' }}>
                  {fileName} \u2014 map source headers to roster fields
                </p>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <StepIndicator current={0} completedSteps={completedSteps} onNavigate={onNavigate} />
              <button onClick={() => setIsDark(d => !d)} title={isDark ? 'Light mode' : 'Dark mode'} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button onClick={() => setIsFullscreen(f => !f)} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button onClick={onClose} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <X size={17} />
              </button>
            </div>
          </div>

          {/* Summary stats */}
          <div style={{ display:'flex', gap:10 }}>
            {[
              { label:'Standard Fields', value: mappings.length, color:'rgba(255,255,255,0.95)' },
              { label:'AI Mapped',       value: aiMapped,        color:'#86efac' },
              { label:'Mapped',          value: mapped,          color:'#93c5fd' },
              { label:'Needs Review',    value: reviewNeeded,    color:'#fde68a' },
              { label:'Not Mapped',      value: unmapped,        color:'#fca5a5' },
            ].map(s => (
              <div key={s.label} style={{ flex:1, background:'rgba(255,255,255,0.1)', borderRadius:9, padding:'9px 13px', border:'1px solid rgba(255,255,255,0.14)' }}>
                <div style={{ fontSize:22, fontWeight:800, color:s.color, lineHeight:1 }}>{s.value}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', marginTop:3, fontWeight:500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {mappings.length === 0 ? (
            <div style={{ padding:48, textAlign:'center', color: t.emptyText, fontSize:14 }}>
              No columns detected — verify the uploaded file has a valid header row.
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>
                  {['#','Target Column','Roster Header (Excel Column)','Confidence','Status','Sample Values'].map(h => (
                    <th key={h} style={{
                      padding:'11px 16px', textAlign:'left',
                      background: t.theadBg, borderBottom:`2px solid ${t.theadBorder}`,
                      color: t.theadColor, fontWeight:700, fontSize:11,
                      textTransform:'uppercase', letterSpacing:'0.6px',
                      position:'sticky', top:0, zIndex:1, whiteSpace:'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mappings.map((m, i) => (
                  <tr key={m.id} className="hm-tr">
                    <td style={{ padding:'10px 16px', borderBottom:`1px solid ${t.rowBorder}`, color: t.rowNumColor, fontWeight:600, width:36 }}>{i+1}</td>
                    {/* Target Column: fixed standard roster field */}
                    <td style={{ padding:'10px 16px', borderBottom:`1px solid ${t.rowBorder}`, minWidth:180 }}>
                      <span style={{
                        display:'inline-block', fontWeight:700, fontSize:13,
                        color: t.targetBadgeColor, background: t.targetBadgeBg,
                        border:`1.5px solid ${t.targetBadgeBorder}`, borderRadius:6,
                        padding:'3px 10px', letterSpacing:'0.02em',
                      }}>
                        {m.systemField}
                      </span>
                    </td>
                    {/* Roster Header: Excel column names as options, pre-filled by API */}
                    <td style={{ padding:'10px 16px', borderBottom:`1px solid ${t.rowBorder}`, minWidth:220 }}>
                      <select
                        className="hm-select"
                        value={m.sourceCol}
                        onChange={e => handleColChange(m.id, e.target.value)}
                        style={{ borderColor: m.sourceCol === NONE ? '#fca5a5' : m.requiresReview ? '#fde68a' : '#e5e7eb' }}
                      >
                        <option value={NONE}>— Not in file —</option>
                        {columns.map(c => {
                          const usedByOther = mappings.some(other => other.id !== m.id && other.sourceCol === c);
                          return (
                            <option key={c} value={c} disabled={usedByOther}>
                              {usedByOther ? `${c} (in use)` : c}
                            </option>
                          );
                        })}
                      </select>
                      <div style={{ marginTop:4, display:'flex', gap:4 }}>
                        {m.isAutoMapped && (
                          <span style={{ fontSize:10, fontWeight:700, color: t.aiBadgeColor, background: t.aiBadgeBg, padding:'1px 6px', borderRadius:6 }}>AI</span>
                        )}
                        {m.requiresReview && (
                          <span style={{ fontSize:10, fontWeight:700, color: t.reviewBadgeColor, background: t.reviewBadgeBg, padding:'1px 6px', borderRadius:6, display:'inline-flex', alignItems:'center', gap:2 }}>
                            <AlertTriangle size={9} /> Review
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding:'10px 16px', borderBottom:`1px solid ${t.rowBorder}`, whiteSpace:'nowrap' }}>
                      {m.confidenceScore !== undefined ? (
                        <span style={{
                          display:'inline-block', padding:'3px 8px', borderRadius:6, fontSize:12, fontWeight:700,
                          background: m.confidenceScore >= 0.9 ? '#dcfce7' : m.confidenceScore >= 0.7 ? '#fef9c3' : '#fee2e2',
                          color:      m.confidenceScore >= 0.9 ? '#15803d' : m.confidenceScore >= 0.7 ? '#854d0e' : '#b91c1c',
                        }}>
                          {Math.round(m.confidenceScore * 100)}%
                        </span>
                      ) : <span style={{ color:'#cbd5e1', fontSize:12 }}>—</span>}
                    </td>
                    <td style={{ padding:'10px 16px', borderBottom:`1px solid ${t.rowBorder}` }}>
                      <MappingBadge systemField={m.systemField} />
                    </td>
                    {/* Sample Values: collapsed by default */}
                    <td style={{ padding:'10px 16px', borderBottom:`1px solid ${t.rowBorder}`, maxWidth:220 }}>
                      {m.sampleValues.length > 0 ? (
                        <>
                          <button
                            onClick={() => toggleExpand(m.id)}
                            style={{ background:'none', border:`1px solid ${t.sampleBorder}`, borderRadius:5, padding:'2px 8px', fontSize:11, color: t.sampleColor, cursor:'pointer', fontWeight:600 }}
                          >
                            {expandedRows.has(m.id) ? '▲ Hide' : `▼ ${m.sampleValues.length} value${m.sampleValues.length > 1 ? 's' : ''}`}
                          </button>
                          {expandedRows.has(m.id) && (
                            <div style={{ marginTop:5 }}>
                              {m.sampleValues.map((v, vi) => (
                                <span key={vi} style={{ display:'inline-block', background: t.sampleBg, border:`1px solid ${t.sampleBorder}`, borderRadius:4, padding:'1px 6px', margin:'1px 3px 1px 0', fontSize:11, color: t.sampleColor }}>{v}</span>
                              ))}
                            </div>
                          )}
                        </>
                      ) : <span style={{ color: t.noDataColor, fontSize:12 }}>{m.sourceCol === NONE ? '—' : 'No data'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ padding:'16px 24px', background: t.footerBg, borderTop:`1px solid ${t.footerBorder}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ fontSize:13, color: t.footerText }}>
            <strong style={{ color: t.targetBadgeColor }}>{mapped}</strong> of <strong>{mappings.length}</strong> standard fields mapped
            {unmapped > 0 && <span style={{ marginLeft:6, color:'#ef4444' }}>({unmapped} not mapped)</span>}
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button
              onClick={onClose}
              style={{ padding:'10px 20px', borderRadius:10, border:`2px solid ${t.cancelBorder}`, background: t.cancelBg, color: t.cancelColor, fontSize:14, fontWeight:600, cursor:'pointer', transition:'border-color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='#93c5fd'}
              onMouseLeave={e => e.currentTarget.style.borderColor= t.cancelBorder}
            >
              Cancel
            </button>
            <button
              onClick={() => !submitted && onConfirm(mappings)}
              disabled={submitted}
              style={{
                padding: '11px 28px', borderRadius: 14,
                border: 'none',
                background: submitted
                  ? '#94a3b8'
                  : 'linear-gradient(135deg, #0f2156 0%, #1e3a8a 100%)',
                color: 'white', fontSize: 14, fontWeight: 600,
                cursor: submitted ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
                boxShadow: submitted ? 'none' : '0 2px 10px rgba(15,33,86,0.30)',
                opacity: submitted ? 0.65 : 1,
                letterSpacing: '0.02em',
                transition: 'transform 0.15s, box-shadow 0.15s, filter 0.15s',
              }}
              onMouseEnter={e => { if (!submitted) { e.currentTarget.style.filter='brightness(1.15)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(15,33,86,0.40)'; e.currentTarget.style.transform='translateY(-1px)'; } }}
              onMouseLeave={e => { e.currentTarget.style.filter='brightness(1)'; e.currentTarget.style.boxShadow= submitted ? 'none' : '0 2px 10px rgba(15,33,86,0.30)'; e.currentTarget.style.transform='translateY(0)'; }}
            >
              {submitted
                ? <><CheckCircle size={16} />&nbsp;Submitted</>
                : <><ClipboardCheck size={16} /><span>Attest</span><ChevronRight size={15} strokeWidth={2.5} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
